// Programas anuales (lote C, M48) → Checklist del Anexo 1: TH-08, PO-02, PO-14, PO-24 y PO-26 (programa del año en
// curso: versión aprobada y firmada, cronograma y ejecución con respaldo) y PO-25 y PO-27 (registro en el SUT con
// constancia). Las reglas están en prog-logica.ts (plazos sugeridos por StorSafe, no normativos; por validar con Kevin).
// PO-14 (programa anual de inspecciones internas): el mismo criterio; cada inspección firmada de una programación
// ejecuta su actividad, y se conserva como traza lo que calculaba Inspecciones v2 (programaciones activas). Desde el
// lote C lo escribe solo este archivo (insp-checklist.ts ya no escribe PO-14).
// Estado manual (misma regla que el lote B y el Plan de Emergencias): un estado marcado a mano (auto_source NULL)
// nunca se sobrescribe; el Checklist muestra "el cálculo automático dice: X" y el botón "Usar cálculo automático".
// Nunca se toca un "No aplica". La aplicabilidad la sigue decidiendo el motor (p. ej. PO 24 a PO 27 desde 11
// trabajadores): este archivo no la cambia. Con la M48 en la base: PO-14 se calcula siempre; los demás ítems, solo en
// empresas que ya usan los programas anuales (al menos un programa en cualquier año). Sin la M48 no toca nada.

import { supabase } from '../../supabaseClient';
import { diaEc } from '../fechas';
import { resultadoPO14 } from '../inspecciones/insp-checklist';
import { esManual, type EstadoGuardadoChecklist, type EstadoItem, type TrazasPlanV2 } from '../plan-emergencias-compliance-sync';
import { respaldos, resultadoPrograma, resultadoSut, type ResultadoPrograma } from './prog-logica';
import type {
  CapacitacionMin, ExamenMin, FilaActividad, FilaEjecucion, FilaPrograma, FilaSut, FilaVersion, InspeccionMin, ProgramacionMin, TipoPrograma,
} from './prog-tipos';

export const FUENTE_PROGRAMAS = 'programas_anuales';
export const ITEMS_PROGRAMAS = ['TH-08', 'PO-02', 'PO-14', 'PO-24', 'PO-25', 'PO-26', 'PO-27'] as const;
export type ItemProgramas = typeof ITEMS_PROGRAMAS[number];
export const MODULO_PROGRAMAS: Record<ItemProgramas, string> = {
  'TH-08': 'Capacitaciones (programa anual)', 'PO-02': 'Vigilancia de la salud (cronograma de exámenes)',
  'PO-14': 'Inspecciones v2 (programa anual de inspecciones)',
  'PO-24': 'Riesgo psicosocial (programa anual)', 'PO-25': 'Riesgo psicosocial (registro en el SUT)',
  'PO-26': 'Riesgo psicosocial (programa de alcohol, tabaco y drogas)', 'PO-27': 'Riesgo psicosocial (registro en el SUT)',
};

export interface DatosProgramas {
  programas: FilaPrograma[];
  versiones: Pick<FilaVersion, 'id' | 'program_id' | 'version' | 'status' | 'approved_at'>[];
  actividades: FilaActividad[];
  ejecuciones: FilaEjecucion[];
  sut: FilaSut[];
  capacitaciones: CapacitacionMin[];
  examenes: ExamenMin[];
  /** Evidencia vigente del seguimiento psicosocial del año (número de actividad → fecha). */
  evidenciaPsicosocial?: Record<number, string>;
  /** PO 14: programaciones e inspecciones de Inspecciones v2. */
  programaciones?: Pick<ProgramacionMin, 'active' | 'end_date'>[];
  inspecciones?: InspeccionMin[];
  /** companies.created_at: el año del registro, el plazo de aprobación es de 30 días desde el registro (si fue después del 31 de enero). */
  empresaRegistrada?: string | null;
}

/** PO 14: el programa de inspecciones del año con la traza de las programaciones activas (lo que calculaba Inspecciones v2). */
export function resultadoProgramaInspecciones(base: ResultadoPrograma, programaciones: Pick<ProgramacionMin, 'active' | 'end_date'>[], hoy: Date): ResultadoPrograma {
  const p14 = resultadoPO14(programaciones, hoy);
  const activas = Number(p14.trazas[0]?.valor ?? 0);
  const nota = activas > 0 ? `${p14.resumen.split('.')[0]}. Cada inspección firmada ejecuta su actividad del programa.`
    : 'No hay inspecciones programadas en Inspecciones v2: el cronograma del programa sale de las programaciones.';
  return { ...base, trazas: [...base.trazas, { ...p14.trazas[0], nota }] };
}

/** Resultado de cada ítem con los datos de la empresa (sin base de datos). */
export function resultadosProgramas(d: DatosProgramas, hoy: Date = new Date()): Record<ItemProgramas, ResultadoPrograma> {
  const anio = hoy.getFullYear();
  const delAnio = (t: TipoPrograma) => d.programas.find(p => p.kind === t && p.program_year === anio) ?? null;
  const calcular = (t: TipoPrograma): ResultadoPrograma => {
    const programa = delAnio(t);
    const versiones = programa ? d.versiones.filter(v => v.program_id === programa.id) : [];
    const aprobada = (versiones.find(v => v.status === 'aprobada') ?? null) as FilaVersion | null;
    const actividades = aprobada ? d.actividades.filter(a => a.version_id === aprobada.id) : [];
    const ejecuciones = programa ? d.ejecuciones.filter(e => e.program_id === programa.id) : [];
    return resultadoPrograma({
      tipo: t, programa, hayVersion: versiones.length > 0, aprobada, actividades, empresaRegistrada: d.empresaRegistrada,
      respaldos: respaldos(t, anio, actividades, { ejecuciones, capacitaciones: d.capacitaciones, examenes: d.examenes,
        evidenciaPsicosocial: t === 'psicosocial' ? d.evidenciaPsicosocial : undefined,
        inspecciones: t === 'inspecciones' ? d.inspecciones : undefined }),
    }, hoy);
  };
  const sutDe = (t: 'psicosocial' | 'sustancias') => {
    const programa = delAnio(t);
    return resultadoSut(t, programa, programa ? d.sut.find(s => s.program_id === programa.id) ?? null : null, hoy);
  };
  return {
    'TH-08': calcular('capacitacion'), 'PO-02': calcular('examenes'),
    'PO-14': resultadoProgramaInspecciones(calcular('inspecciones'), d.programaciones ?? [], hoy),
    'PO-24': calcular('psicosocial'), 'PO-25': sutDe('psicosocial'), 'PO-26': calcular('sustancias'), 'PO-27': sutDe('sustancias'),
  };
}

// ¿Qué se escribe? null = nada. Lo manual (auto_source NULL) nunca se reemplaza; "No aplica" tampoco.
export function decisionProgramas(existente: EstadoGuardadoChecklist | undefined, deseado: EstadoItem): EstadoItem | null {
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null;
  if (existente?.estado === deseado && existente.auto_source === FUENTE_PROGRAMAS) return null;
  if (!existente && deseado === 'pendiente') return null;
  return deseado;
}

/** Cómo se muestra en el Checklist según lo guardado. */
export function presentacionProgramas(item: ItemProgramas, existente: EstadoGuardadoChecklist | undefined, r: ResultadoPrograma) {
  const manual = esManual(existente) && existente!.estado !== r.estado;
  return {
    estado: r.estado, resumen: r.resumen, trazas: r.trazas, manualRespetado: manual, estadoManual: manual ? existente!.estado : null,
    modulo: MODULO_PROGRAMAS[item], fuente: FUENTE_PROGRAMAS,
  };
}

/** Evidencia vigente del seguimiento psicosocial (tablas de septiembre) del año: número de actividad → fecha. */
export async function evidenciaPsicosocialDelAnio(companyId: string, anio: number): Promise<Record<number, string>> {
  const { data: ciclo, error } = await supabase.from('psychosocial_program_cycles').select('id').eq('company_id', companyId)
    .eq('program_year', anio).maybeSingle();
  if (error || !ciclo) return {};
  const id = (ciclo as { id: string }).id;
  const [{ data: acts }, { data: evs }] = await Promise.all([
    supabase.from('psychosocial_program_activities').select('id, activity_no').eq('cycle_id', id),
    supabase.from('psychosocial_program_evidence').select('activity_id, uploaded_at, voided').eq('cycle_id', id),
  ]);
  const numero = new Map(((acts ?? []) as { id: string; activity_no: number }[]).map(a => [a.id, a.activity_no]));
  const out: Record<number, string> = {};
  ((evs ?? []) as { activity_id: string; uploaded_at: string; voided: boolean }[]).filter(e => !e.voided).forEach(e => {
    const n = numero.get(e.activity_id);
    const f = diaEc(e.uploaded_at);
    if (n && (!out[n] || f < out[n])) out[n] = f;
  });
  return out;
}

/** Lee todo lo de la empresa para calcular (null si la base no tiene la M48 o no hay permiso). */
export async function cargarDatosProgramas(companyId: string, hoy: Date = new Date()): Promise<DatosProgramas | null> {
  const { data: programas, error } = await supabase.from('annual_programs').select('id, company_id, kind, program_year, created_at').eq('company_id', companyId);
  if (error) return null;
  const progs = (programas ?? []) as FilaPrograma[];
  // PO 14: programaciones e inspecciones de Inspecciones v2 (sin la M41 o sin permiso, quedan vacías).
  const [{ data: sch }, { data: ins }, { data: emp }] = await Promise.all([
    supabase.from('insp_schedules').select('active, end_date').eq('company_id', companyId),
    supabase.from('insp_inspections').select('id, schedule_id, template_name, status, scheduled_date, performed_date, signed_at').eq('company_id', companyId),
    supabase.from('companies').select('created_at').eq('id', companyId).maybeSingle(),
  ]);
  const programaciones = (sch ?? []) as DatosProgramas['programaciones'];
  const inspecciones = (ins ?? []) as InspeccionMin[];
  const empresaRegistrada = (emp as { created_at?: string } | null)?.created_at ?? null;
  if (progs.length === 0) return { programas: [], versiones: [], actividades: [], ejecuciones: [], sut: [], capacitaciones: [], examenes: [], programaciones, inspecciones,
    empresaRegistrada };
  const ids = progs.map(p => p.id);
  const anio = hoy.getFullYear();
  const usaPsicosocial = progs.some(p => p.kind === 'psicosocial' && p.program_year === anio);
  const [{ data: versiones, error: e1 }, { data: actividades, error: e2 }, { data: ejecuciones, error: e3 }, { data: sut, error: e4 },
    { data: caps }, { data: exams }, evidenciaPsicosocial] = await Promise.all([
    supabase.from('annual_program_versions').select('id, program_id, version, status, approved_at').in('program_id', ids),
    supabase.from('annual_program_activities').select('*').in('program_id', ids),
    supabase.from('annual_program_executions').select('*').in('program_id', ids),
    supabase.from('annual_program_sut').select('*').in('program_id', ids),
    supabase.from('sso_trainings').select('id, title, scheduled_date, status').eq('company_id', companyId),
    supabase.from('worker_medical_exams').select('id, worker_id, exam_date, exam_type').eq('company_id', companyId),
    usaPsicosocial ? evidenciaPsicosocialDelAnio(companyId, anio).catch(() => ({})) : Promise.resolve({}),
  ]);
  if (e1 || e2 || e3 || e4) return null;
  return {
    programas: progs, versiones: (versiones ?? []) as DatosProgramas['versiones'], actividades: (actividades ?? []) as FilaActividad[],
    ejecuciones: (ejecuciones ?? []) as FilaEjecucion[], sut: (sut ?? []) as FilaSut[], capacitaciones: (caps ?? []) as CapacitacionMin[],
    examenes: (exams ?? []) as ExamenMin[], evidenciaPsicosocial, programaciones, inspecciones, empresaRegistrada,
  };
}

/** Ítems que se calculan: PO-14 siempre (con la M48); los demás, solo si la empresa ya usa los programas anuales. */
export const itemsACalcular = (d: Pick<DatosProgramas, 'programas'>): ItemProgramas[] =>
  d.programas.length > 0 ? [...ITEMS_PROGRAMAS] : ['PO-14'];

export async function syncProgramasCompliance(companyId: string, hoy: Date = new Date()): Promise<TrazasPlanV2> {
  if (!companyId) return {};
  const datos = await cargarDatosProgramas(companyId, hoy);
  // Sin M48 en la base (o sin permiso): no se toca nada.
  if (!datos) return {};
  const items = itemsACalcular(datos);
  const { data: existentes } = await supabase.from('checklist_item_estados').select('item_id, estado, auto_source')
    .eq('company_id', companyId).in('item_id', items);
  const res = resultadosProgramas(datos, hoy);
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const ahora = new Date().toISOString();
  const filas = items.flatMap(item => {
    const d = decisionProgramas(porItem.get(item), res[item].estado);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_PROGRAMAS, updated_at: ahora }] : [];
  });
  if (filas.length) {
    const { error } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (error) console.error('syncProgramasCompliance', error);
  }
  return Object.fromEntries(items.map(item => [item, presentacionProgramas(item, porItem.get(item), res[item])]));
}

// "Usar cálculo automático": quita el estado manual y escribe el cálculo de los programas. Nunca sobre "No aplica".
export async function aplicarCalculoProgramas(companyId: string, itemId: string, estado: EstadoItem): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', itemId).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de ${itemId}: ${error.message}`);
  if ((data as { estado: string } | null)?.estado === 'no_aplica') throw new Error(`${itemId} está marcado como "No aplica": no se cambia.`);
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: itemId, estado, auto_source: FUENTE_PROGRAMAS, updated_at: new Date().toISOString() },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(e2.code === '42501' ? 'No tienes permiso para cambiar el Checklist.' : `No se pudo aplicar el cálculo en ${itemId}: ${e2.message}`);
}
